// frontend/src/components/layout/Header.jsx
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { ROUTES } from "../../config";
import ThemeToggle from "../common/ThemeToggle";
import Button from "../common/Button";
import IconButton from "../common/IconButton";
import { AppIcon, ChevronDownIcon, CloseIcon, LogOutIcon, MenuIcon } from "../common/Icon";
import "./Header.css";

const NAV = [
  { to: "/#how-it-works", label: "How it works" },
  { to: "/#research", label: "Research" },
  { to: ROUTES.privacy, label: "Legal" },
];

function initialsOf(user) {
  const src = (user?.display_name || user?.email || "?").trim();
  return src.charAt(0).toUpperCase();
}

function UserMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        btnRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const name = user?.display_name || user?.email || "Account";

  return (
    <div className="user-menu" ref={ref}>
      <button
        ref={btnRef}
        type="button"
        className="user-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${name}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="user-menu__avatar" aria-hidden="true">{initialsOf(user)}</span>
        <span className="user-menu__name">{name}</span>
        <ChevronDownIcon size={14} />
      </button>
      {open && (
        <div className="user-menu__panel" role="menu">
          <div className="user-menu__who">
            <span className="user-menu__who-name">{user?.display_name || "Signed in"}</span>
            <span className="user-menu__who-email">{user?.email}</span>
          </div>
          <Link role="menuitem" to={ROUTES.app} className="user-menu__item" onClick={() => setOpen(false)}>
            <AppIcon size={16} /> Open app
          </Link>
          <button
            role="menuitem"
            type="button"
            className="user-menu__item"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            <LogOutIcon size={16} /> Log out
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Site header.
 * Props:
 *  variant: "site" (default, public pages) | "landing" (transparent over hero) | "app"
 *  onLoginClick: optional; defaults to navigating to /login
 *  onToggleSidebar, sidebarOpen: when provided (app variant) renders a sidebar toggle
 *  actions: extra nodes rendered before the theme toggle (e.g. "New chat")
 *  hideNav: hide the marketing nav links
 * Legacy v1 props (onNewChat, onOpenArchives, …) are accepted and ignored.
 */
export default function Header({ variant = "site", onLoginClick, onToggleSidebar, sidebarOpen, actions, hideNav }) {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const isApp = variant === "app";
  const showNav = !hideNav && !isApp;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogin = () => {
    if (onLoginClick) onLoginClick();
    else navigate(ROUTES.login, { state: { from: location } });
  };

  const handleLogout = async () => {
    await logout();
    navigate(ROUTES.home);
  };

  return (
    <header
      className={`site-header site-header--${variant}${scrolled ? " is-scrolled" : ""}${mobileOpen ? " is-menu-open" : ""}`}
    >
      <div className={`site-header__inner${isApp ? " site-header__inner--fluid" : " container"}`}>
        <div className="site-header__left">
          {onToggleSidebar && (
            <IconButton
              label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
              icon={<MenuIcon size={18} />}
              onClick={onToggleSidebar}
              aria-expanded={!!sidebarOpen}
              className="site-header__sidebar-toggle"
            />
          )}
          <Link to={ROUTES.home} className="brand" aria-label="PAWNI home">
            <img src="/pawni-logo.png" alt="" className="brand__logo" width="28" height="28" />
            <span className="brand__text">
              <span className="brand__word">PAWNI</span>
              <span className="brand__sub">Prompt Architecture Wizard</span>
            </span>
          </Link>
        </div>

        {showNav && (
          <nav className="site-nav" aria-label="Primary">
            <ul role="list" className="site-nav__list">
              {NAV.map((n) => (
                <li key={n.label}>
                  <Link to={n.to} className="site-nav__link">
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        )}

        <div className="site-header__right">
          {actions}
          <ThemeToggle />
          {isAuthenticated ? (
            <UserMenu user={user} onLogout={handleLogout} />
          ) : (
            <Button variant={variant === "landing" ? "secondary" : "primary"} size="sm" onClick={handleLogin} className="site-header__login">
              Log in
            </Button>
          )}
          {showNav && (
            <IconButton
              label={mobileOpen ? "Close menu" : "Open menu"}
              icon={mobileOpen ? <CloseIcon size={18} /> : <MenuIcon size={18} />}
              onClick={() => setMobileOpen((o) => !o)}
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav"
              className="site-header__menu-btn"
            />
          )}
        </div>
      </div>

      {showNav && mobileOpen && (
        <nav id="mobile-nav" className="mobile-nav" aria-label="Mobile">
          <ul role="list">
            {NAV.map((n) => (
              <li key={n.label}>
                <Link to={n.to} className="mobile-nav__link" onClick={() => setMobileOpen(false)}>
                  {n.label}
                </Link>
              </li>
            ))}
            {isAuthenticated && (
              <li>
                <Link to={ROUTES.app} className="mobile-nav__link" onClick={() => setMobileOpen(false)}>
                  Open app
                </Link>
              </li>
            )}
          </ul>
        </nav>
      )}
    </header>
  );
}
